import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { SchemaProvider, useSchema } from "./context/SchemaContext";
import { ConnectionPanel } from "./components/ConnectionPanel";
import { EventsTable } from "./components/EventsTable";
import { EventDetailDialog } from "./components/event-detail-dialog-component";
import { InstructionsView } from "./components/insturctions-component";
import { ConnectionsDialog } from "./components/connections-dialog.component";
import { ConnectionsHelpDialog } from "./components/connections-help-dialog.component";
import { useEventForm } from "./hooks/useEventForm";
import { useNugetVersions } from "./hooks/useNugetVersions";
import { getDisplayEventName, useEventKeys } from "./utils/schema.util";
import { APP_COORD_PREFIX, WISR_EVENTS_PREFIX } from "./utils/config";
import { EventCategory } from "./types";

function PublishEventContent() {
  const { loading, error } = useSchema();
  const nugetVersions = useNugetVersions();
  const wisrEventKeys = useEventKeys(WISR_EVENTS_PREFIX);
  const appCoordEventKeys = useEventKeys(APP_COORD_PREFIX);

  const [selectedCategory, setSelectedCategory] = useState<EventCategory>("wisr");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const {
    serviceBusString,
    setServiceBusString,
    formValues,
    selectedEvent,
    setSelectedEvent,
    selectedConnectionName,
    connectionsDialogOpen,
    setConnectionsDialogOpen,
    connectionsHelpOpen,
    setConnectionsHelpOpen,
    handleChange,
    handlePublish,
    handleSelectConnection,
    handleClearConnection,
  } = useEventForm();

  const eventsByCategory = useMemo(
    () => ({ wisr: wisrEventKeys, appCoordinator: appCoordEventKeys }),
    [wisrEventKeys, appCoordEventKeys]
  );

  const filteredEvents = useMemo(() => {
    if (selectedCategory === "instructions") return [];
    const events = eventsByCategory[selectedCategory];
    if (!searchQuery) return events;
    return events.filter((key) =>
      getDisplayEventName(key).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [selectedCategory, searchQuery, eventsByCategory]);

  const paginatedEvents = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEvents.slice(start, start + rowsPerPage);
  }, [filteredEvents, page]);

  const handleCategoryChange = (category: EventCategory) => {
    setSelectedCategory(category);
    setPage(0);
    setSearchQuery("");
  };

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          p: 3,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography color="error" variant="h6" gutterBottom>
            Failed to load schema
          </Typography>
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error.message}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Check the browser console for more details. Common issues:
            <br />• CORS error - verify your API has CORS enabled and correct origin allowed
            <br />• SSL certificate - if using https://localhost, ensure certificate is trusted
            <br />• API URL - verify REACT_APP_BASE_URL in .env.local is correct
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Publish Event
        </Typography>
        <Button
          size="small"
          color={selectedCategory === "instructions" ? "warning" : "inherit"}
          onClick={() => handleCategoryChange("instructions")}
          sx={{ textTransform: "none" }}
        >
          Your event not here?
        </Button>
      </Box>

      <Tabs
        value={selectedCategory === "instructions" ? false : selectedCategory}
        onChange={(_, value) => handleCategoryChange(value)}
        sx={{ mb: 2 }}
      >
        <Tab
          value="wisr"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>Wisr Events</span>
              <Chip label={eventsByCategory.wisr.length} size="small" color="primary" />
              {nugetVersions.wisrEvents && (
                <Typography variant="caption" color="text.secondary">
                  v{nugetVersions.wisrEvents}
                </Typography>
              )}
            </Box>
          }
        />
        <Tab
          value="appCoordinator"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>App Coordinator</span>
              <Chip label={eventsByCategory.appCoordinator.length} size="small" color="secondary" />
              {nugetVersions.wisrApplicationCoordinatorEvents && (
                <Typography variant="caption" color="text.secondary">
                  v{nugetVersions.wisrApplicationCoordinatorEvents}
                </Typography>
              )}
            </Box>
          }
        />
      </Tabs>
      <Divider sx={{ mb: 3 }} />

      {selectedCategory === "instructions" ? (
        <InstructionsView />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Alert
              severity="info"
              variant="outlined"
              icon={<VpnKeyIcon fontSize="small" />}
              sx={{ flexGrow: 1, minWidth: 260, py: 0.5 }}
            >
              <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0 }}>
                New: Saved Connections
              </AlertTitle>
              <Typography variant="caption" component="span">
                Copy paste !? Don't need that.{" "}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => setConnectionsHelpOpen(true)}
                sx={{ p: 0, fontSize: "0.72rem", textTransform: "none", minWidth: 0 }}
              >
                How to add your own →
              </Button>
            </Alert>

            <Button
              variant="contained"
              disableElevation
              startIcon={
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/azure-service-bus.png`}
                  alt=""
                  sx={{ width: 20, height: 20, objectFit: "contain" }}
                />
              }
              onClick={() => setConnectionsDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
                backgroundColor: "#263238",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#37474f" },
              }}
            >
              Bus Connections
              {selectedConnectionName && (
                <Chip
                  label={selectedConnectionName}
                  size="small"
                  sx={{
                    ml: 1.5,
                    height: 22,
                    backgroundColor: "rgba(255,255,255,0.25)",
                    color: "white",
                    fontWeight: 500,
                  }}
                />
              )}
            </Button>
          </Box>

          <ConnectionPanel
            serviceBusString={serviceBusString}
            selectedConnectionName={selectedConnectionName}
            onChange={setServiceBusString}
            onClear={handleClearConnection}
          />
          <EventsTable
            selectedCategory={selectedCategory}
            paginatedEvents={paginatedEvents}
            filteredEventsCount={filteredEvents.length}
            searchQuery={searchQuery}
            page={page}
            rowsPerPage={rowsPerPage}
            onSearchChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            onPageChange={(_, newPage) => setPage(newPage)}
            onSelectEvent={setSelectedEvent}
          />
        </>
      )}

      {selectedEvent && (
        <EventDetailDialog
          open
          eventName={selectedEvent}
          formValues={formValues}
          onClose={() => setSelectedEvent(null)}
          handleChange={handleChange}
          handlePublish={handlePublish}
        />
      )}

      <ConnectionsDialog
        open={connectionsDialogOpen}
        selectedName={selectedConnectionName}
        onClose={() => setConnectionsDialogOpen(false)}
        onSelect={handleSelectConnection}
      />

      <ConnectionsHelpDialog
        open={connectionsHelpOpen}
        onClose={() => setConnectionsHelpOpen(false)}
      />
    </>
  );
}

export function PublishEventPage() {
  return (
    <SchemaProvider>
      <PublishEventContent />
    </SchemaProvider>
  );
}
